// A2/NG1 — reversible affine mapping arbitrary data XY into the safe MapLibre display box
// (well inside ±180 lng / ±85 lat). Used for planar mode (non-WGS84 / projected / unknown SRID)
// so MapLibre — which only renders WGS84 lng/lat — can still show local coordinates, while T28's
// probe/measure recover ORIGINAL data-space values through `inverse`. Pure module.

import type { PlanarTransform } from './spatialMapTypes';

// Safe display box: margin inside the ±180/±85 Web-Mercator-renderable envelope so fitted data
// never lands on the clamp edge.
const DISPLAY = { lngMin: -170, lngMax: 170, latMin: -80, latMax: 80 } as const;

type Axis = { forward: (v: number) => number; inverse: (d: number) => number };

function makeAxis(min: number, span: number, dMin: number, dMax: number): Axis {
  // Degenerate / invalid extent → collapse to the display-range start so a single point renders
  // at a stable spot and round-trips back to its original value (no divide-by-zero).
  if (!(span > 0) || !Number.isFinite(span)) {
    return { forward: () => dMin, inverse: () => min };
  }
  const scale = (dMax - dMin) / span;
  return {
    forward: (v) => dMin + (v - min) * scale,
    inverse: (d) => min + (d - dMin) / scale,
  };
}

/**
 * @param dataBbox `[minX, minY, maxX, maxY]` in the source coordinate system.
 * @returns a `{forward, inverse}` pair where `inverse(forward(xy))` round-trips within 1e-6 for a
 * non-degenerate extent, every mapped point stays inside the safe display box, and a degenerate
 * single-point extent never divides by zero.
 */
export function computePlanarTransform(
  dataBbox: [number, number, number, number],
): PlanarTransform {
  const [minX, minY, maxX, maxY] = dataBbox;
  const ax = makeAxis(minX, maxX - minX, DISPLAY.lngMin, DISPLAY.lngMax);
  const ay = makeAxis(minY, maxY - minY, DISPLAY.latMin, DISPLAY.latMax);
  return {
    forward: ([x, y]) => [ax.forward(x), ay.forward(y)],
    inverse: ([lng, lat]) => [ax.inverse(lng), ay.inverse(lat)],
  };
}
